import React, { useState, useEffect, useRef } from 'react'; // Keep React imports
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Settings } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import PracticeSettings from '../PracticeSettings';
import { usePracticeData } from '../../contexts/PracticeDataContext';
import { useInvoice } from '../../pages/InvoiceApp';
import { doc, updateDoc } from 'firebase/firestore'; // Add Firestore imports
import { db } from '../../firebase'; // Add Firebase db import
import { useTheme } from '../ui/theme-provider'; // Import useTheme
import { cn } from '../../lib/utils'; // Import cn

// Use React.memo to prevent unnecessary rerenders
export const PracticeSelector = React.memo(({ onSelectPractice, onOpenSettings }) => {
  // Get data from contexts
  const { 
    selectedPractices, 
    hasUpdates,
    loadUserSelectedPractices,
    updateSelectedPractices, // Add this to update context
    timestamp,
    forceUpdatePracticeData
  } = usePracticeData();
  
  const {
    selectedPractice,
    user,
    incrementRenderCount
  } = useInvoice();
  
  // Local state
  const [componentKey, setComponentKey] = useState('practice-select-initial');
  const [showPracticeSettings, setShowPracticeSettings] = useState(false);
  const [savingPractices, setSavingPractices] = useState(false);
  const { theme } = useTheme(); // Get theme
  
  // Refs for tracking state
  const shouldLogRef = useRef(true);
  const prevTimestampRef = useRef(timestamp);
  const contentHashRef = useRef(null);
  
  // Track render count (debug only)
  incrementRenderCount?.('practiceSelector');
  
  // Calculate a content hash from selectedPractices to detect changes
  const calculateSelectedPracticesHash = () => {
    if (!selectedPractices?.length) return '';
    return selectedPractices
      .map(p => `${p.prac_code}:${p.prac_name || ''}`)
      .join('|');
  };
  
  // ENHANCED: Update component key when timestamp OR selectedPractices content changes
  useEffect(() => {
    const currentHash = calculateSelectedPracticesHash();
    const timestampChanged = timestamp !== prevTimestampRef.current;
    const contentChanged = currentHash !== contentHashRef.current;
    
    if (timestampChanged || contentChanged) {
      if (timestampChanged) {
        console.log(`PracticeSelector: Timestamp changed from ${prevTimestampRef.current} to ${timestamp}`);
        prevTimestampRef.current = timestamp;
      }
      
      if (contentChanged) {
        console.log('PracticeSelector: Practice content changed, updating component key');
        contentHashRef.current = currentHash;
      }
      
      // Generate a unique key using both timestamp and content hash
      const newKey = `practice-select-${timestamp}-${Date.now()}`;
      console.log(`PracticeSelector: Updating component key to ${newKey}`);
      setComponentKey(newKey);
    }
  }, [timestamp, selectedPractices]);
  
  // Load practices when component mounts if needed
  useEffect(() => {
    if (user?.uid && (!selectedPractices || selectedPractices.length === 0)) {
      console.log('PracticeSelector: Loading user practices - none currently loaded');
      loadUserSelectedPractices(user.uid);
    }
  }, [user, loadUserSelectedPractices, selectedPractices]);
  
  // Add new effect to handle updates when hasUpdates becomes true
  useEffect(() => {
    if (hasUpdates && selectedPractices.length > 0) {
      console.log('🔄 Updates available, checking for practice V06985');
      
      // Check if V06985 is in our selected practices
      const v06985 = selectedPractices.find(p => 
        (typeof p === 'object' ? p.prac_code : p) === 'V06985'
      );
      
      if (v06985) {
        console.log('🔄 Found V06985 in selected practices, forcing direct update');
        forceUpdatePracticeData('V06985');
      }
    }
  }, [hasUpdates, selectedPractices, forceUpdatePracticeData]);
  
  // FIXED: Properly handle saving practices to Firebase and updating context
  const handleSavePractices = async (practices, newlyAddedPracticeCode = null) => {
    if (!user?.uid) {
      console.error('[PracticeSelector] Cannot save practices: No user ID available');
      return;
    }
    
    try {
      setSavingPractices(true);
      console.log('[PracticeSelector] handleSavePractices called', {
        practicesCount: practices?.length,
        newlyAddedPracticeCode,
        userId: user?.uid
      });
      
      // 1. Update Firebase with practice codes only (not full objects)
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        // Store only practice codes to keep document size small
        selectedPractices: practices.map(p => typeof p === 'object' ? p.prac_code : p),
        lastUpdated: new Date()
      });
      console.log('[PracticeSelector] ✅ Firestore save complete');
      
      // 2. Update context state with full practice objects
      updateSelectedPractices(practices);
      
      // 3. Close the dialog
      setShowPracticeSettings(false);
      
      // 4. Update the selected practice
      if (practices.length > 0) {
        if (newlyAddedPracticeCode && onSelectPractice) {
          // Auto-select newly added practice
          console.log('[PracticeSelector] Auto-selecting newly added:', newlyAddedPracticeCode);
          onSelectPractice(newlyAddedPracticeCode);
        } else {
          // If current selected practice is not in the new list, select the first one
          const currentStillExists = practices.some(p => 
            (typeof p === 'object' ? p.prac_code : p) === selectedPractice
          );
          
          if (!currentStillExists && onSelectPractice) {
            const firstPracticeCode = typeof practices[0] === 'object' 
              ? practices[0].prac_code 
              : practices[0];
            onSelectPractice(firstPracticeCode);
          }
        }
      }
      
      console.log('[PracticeSelector] Practices saved successfully');
    } catch (error) {
      console.error('[PracticeSelector] ❌ Error saving practices:', error);
    } finally {
      setSavingPractices(false);
    }
  };
  
  // Add logging when rendering items for debugging
  const renderPracticeItems = () => {
    // Log practices being rendered (only occasionally)
    if (shouldLogRef.current) {
      console.log(`🖥️ Rendering ${selectedPractices.length} practice items with key ${componentKey}`);
      console.log('🖥️ selectedPractices for rendering:', selectedPractices);
      
      // Disable logging for a short period to prevent spam
      shouldLogRef.current = false;
      setTimeout(() => { shouldLogRef.current = true; }, 2000);
    }
    
    return selectedPractices.map(practice => (
      <SelectItem 
        key={`${practice.prac_code}-${componentKey}`}
        value={practice.prac_code}
        // Item: Theme-aware
        className={cn(
          "text-xs sm:text-sm",
          theme === 'light' 
            ? 'text-black hover:bg-gray-100 focus:bg-gray-100' 
            : 'text-white hover:bg-purple-500/20 focus:bg-purple-500/20'
        )}
      >
        {practice.prac_name || practice.prac_code}
        {practice.postcode ? ` (${practice.postcode})` : ''}
      </SelectItem>
    ));
  };
  
  // Log component renders in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`🖥️ PracticeSelector RENDER with timestamp: ${timestamp}`);
  }

  return (
    <>
      {/* Card: Theme-aware */}
      <Card className={cn(
        "shadow-lg",
        theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3F] border-purple-500/20'
      )}>
        <CardContent className="p-3 sm:p-4">
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            {/* Title: Theme-aware */}
            <h2 className={cn(
              "text-base sm:text-lg font-medium",
              theme === 'light' ? 'text-black' : 'text-white'
            )}>Select Practice</h2>
          </div>
          
          <div className="grid grid-cols-1 gap-2 sm:gap-4">
            <div className="flex items-end gap-2">
              <div className="flex-grow">
                {/* Create completely new Select on each render by using key */}
                <Select 
                  key={componentKey}
                  value={selectedPractice} 
                  onValueChange={onSelectPractice}
                >
                  {/* Trigger: Theme-aware */}
                  <SelectTrigger id="practice" className={cn(
                    "text-xs sm:text-sm",
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black' 
                      : 'bg-[#1F1F2E] border-purple-500/30 text-white'
                  )}>
                    <SelectValue placeholder={selectedPractices.length > 0 ? "Select practice" : "No practices available"} />
                  </SelectTrigger>
                  {/* Content: Theme-aware */}
                  <SelectContent className={cn(
                    theme === 'light' ? 'bg-white border-gray-300' : 'bg-[#2A2A3E] border-purple-500/30'
                  )}>
                    {selectedPractices.length > 0 ? (
                      renderPracticeItems()
                    ) : (
                      // Disabled Item: Theme-aware
                      <SelectItem value="none" disabled className={cn(
                        theme === 'light' ? 'text-gray-400' : 'text-gray-500',
                        'hover:bg-transparent'
                      )}>
                        Please add practices first
                      </SelectItem> 
                    )}
                  </SelectContent>
                </Select>
              </div>
              {/* Manage Button: Theme-aware */}
              <Button 
                onClick={() => setShowPracticeSettings(true)}
                size="sm" 
                variant="outline"
                className={cn(
                  "h-8 sm:h-9",
                  theme === 'light' 
                    ? 'border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100' 
                    : 'border-purple-500/30 bg-purple-900/20 text-purple-300 hover:bg-purple-500/20'
                )}
              >
                <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="ml-1 text-xs">Manage</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Practice Settings Dialog */}
      <Dialog open={showPracticeSettings} onOpenChange={setShowPracticeSettings}>
        {/* Dialog Content: Theme-aware */}
        <DialogContent className={cn(
          "max-w-4xl max-h-[80vh] overflow-auto",
          theme === 'light' ? 'bg-white text-black' : 'bg-[#2A2A3F] border-purple-500/20 text-white'
        )}>
          <DialogHeader>
            {/* Dialog Title: Theme-aware */}
            <DialogTitle className={cn(theme === 'light' ? 'text-black' : 'text-purple-300')}>Manage Practices</DialogTitle>
          </DialogHeader>
          {/* PracticeSettings component should handle its own theme */}
          <PracticeSettings 
            initialSelectedPractices={selectedPractices}
            onSave={handleSavePractices}
            onClose={() => setShowPracticeSettings(false)}
            user={user}
            forceRefresh={true}
          />
        </DialogContent>
      </Dialog>
    </>
  );
});

export default PracticeSelector;
